import { responsive } from '@theme/responsive';

/**
 * <faq-section> — in-page FAQ navigation with scroll-spy and search.
 *
 * - The tab rail is `role="navigation"`; each tab is a link-button that scrolls to its
 *   matching `role="region"` group (fast ease-in jump). All groups stay visible — this is
 *   scroll-spy in-page nav, not a tab widget, so no tabpanel show/hide.
 * - The current tab follows the scroll position via IntersectionObserver (no scroll-handler
 *   layout reads — see COI code-review: scroll-spy → IntersectionObserver), marked with
 *   `aria-current="true"`.
 * - Accordion single-open is handled globally by `@theme/dropdown` (`.Dropdown`).
 * - Search filters questions/answers/group headings; an empty match surfaces a no-results
 *   banner while keeping the full FAQ browsable.
 */

/** Time (ms) the observer ignores updates after a click-triggered scroll, so the clicked tab isn't overridden as groups pass the center line during the jump. */
const OBSERVER_SUPPRESS_MS = 250;
/** Anchor-jump scroll animation length (ms) — fast ease-in per design spec. */
const SCROLL_DURATION_MS = 100;

/**
 * JS↔markup contract, in one place. These are explicit `data-*` hooks (not style classes): a
 * `data-` attribute signals "JS reads this", so a CSS class rename can't silently break search
 * matching. The blocks keep their BEM classes for styling; these attributes are the JS anchors.
 */
const ITEM_SELECTORS = {
  question: '[data-faq-question]',
  answer: '[data-faq-answer]',
  groupHeading: '[data-faq-heading]',
};

class FaqSection extends HTMLElement {
  #initialized = false;
  #faqItems = [];
  #filterTimer = null;
  #groupObserver = null;
  #visibleGroups = new Set();
  #suppressObserverUntil = 0;
  #scrollRAF = 0;

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.searchTerm = '';
  }

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#cacheDom();

    this.addEventListener('click', this.onClick);
    this.searchInput?.addEventListener('input', this.onInput);

    this.initializeTabs();
    this.#bindGroupObserver();
    this.#validateTabLinks();
  }

  disconnectedCallback() {
    if (!this.#initialized) return;
    this.#initialized = false;

    clearTimeout(this.#filterTimer);
    this.#filterTimer = null;
    cancelAnimationFrame(this.#scrollRAF);
    this.#groupObserver?.disconnect();
    this.#groupObserver = null;
    this.#visibleGroups.clear();

    this.removeEventListener('click', this.onClick);
    this.searchInput?.removeEventListener('input', this.onInput);

    this.searchInput = null;
    this.searchStatus = null;
    this.noResults = [];
    this.tabs = [];
    this.groups = [];
    this.tabsRail = null;
    this.swiperEl = null;
    this.#faqItems = [];
    this.searchTerm = '';
  }

  onClick(event) {
    const tab = event.target.closest('[data-action="handle-tab-click"]');
    if (tab) {
      this.handleTabClick(tab);
    }
  }

  onInput(event) {
    this.searchTerm = event.target.value.trim().toLowerCase();

    clearTimeout(this.#filterTimer);

    if (!this.searchTerm) {
      this.#filterTimer = null;
      this.filterBySearch();
      return;
    }

    // timeout: debounce filtering until the user pauses typing (200ms) so we don't re-scan
    // every FAQ item on each keystroke. Cleared on new input and in disconnectedCallback.
    this.#filterTimer = setTimeout(() => this.filterBySearch(), 200);
  }

  initializeTabs() {
    if (!this.tabs.length || !this.groups.length) return;
    const currentHash = window.location.hash.replace('#', '');
    const hashMatch = this.tabs.find((tab) => tab.dataset.toggleId === currentHash);
    const firstTab = hashMatch || this.tabs[0];
    if (!firstTab) return;
    this.setActiveTabById(firstTab.dataset.toggleId);
    // Own the initial scroll too: if the active tab resolved from the URL hash, jump to its group.
    if (hashMatch) requestAnimationFrame(() => this.scrollToGroup(currentHash));
  }

  // Dev-only (theme editor): a tab scrolls to the group whose heading handleizes to the same id.
  // That contract is two hand-typed merchant fields (tab label vs group heading) with no runtime
  // enforcement, so warn when a tab points at nothing — a silent no-op click is otherwise invisible.
  #validateTabLinks() {
    if (!window.Shopify?.designMode) return;
    const groupIds = new Set(this.groups.map((group) => group.dataset.faqGroupId));
    this.tabs.forEach((tab) => {
      const id = tab.dataset.toggleId;
      if (id && !groupIds.has(id)) {
        console.error(
          `[faq] Tab "${id}" has no matching group — clicking it scrolls nowhere. Make the tab label match a group heading exactly.`
        );
      }
    });
  }

  handleTabClick(tab) {
    const targetId = tab.dataset.toggleId;
    if (!targetId) return;

    if (this.searchTerm) {
      this.searchInput.value = '';
      this.searchTerm = '';
      this.resetSearchVisibility();
    }

    this.setActiveTabById(targetId);
    this.scrollToGroup(targetId);
    window.history.replaceState(null, '', `#${targetId}`);
  }

  setActiveTabById(targetId) {
    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-current', tab.dataset.toggleId === targetId ? 'true' : 'false');
    });

    this.#slideActiveTabIntoView();
  }

  // Mobile only: bring the active tab into view in the horizontal swiper. No-op on desktop
  // where the swiper is disabled (breakpoint enabled:false) and tabs stack vertically.
  #slideActiveTabIntoView() {
    const swiper = this.swiperEl?.swiper;
    if (!swiper || swiper.enabled === false) return;
    const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-current') === 'true');
    const slide = activeTab?.closest('.swiper-slide');
    if (!slide) return;
    const index = swiper.slides.indexOf(slide);
    if (index >= 0) swiper.slideTo(index);
  }

  #bindGroupObserver() {
    if (!this.groups.length) return;
    // rootMargin collapses the IO root to a 1px line at the viewport center — the group
    // crossing it is the "current" one. Semantically the same as a center-line scroll spy,
    // but without the per-frame layout flush a scroll handler + getBoundingClientRect causes.
    this.#groupObserver = new IntersectionObserver(
      (entries) => this.onGroupIntersect(entries),
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    this.groups.forEach((group) => this.#groupObserver.observe(group));
  }

  onGroupIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.#visibleGroups.add(entry.target);
      } else {
        this.#visibleGroups.delete(entry.target);
      }
    });

    if (this.searchTerm) return;
    if (performance.now() < this.#suppressObserverUntil) return;

    // Topmost group (document order) currently crossing the center line.
    const active = this.groups.find((group) => this.#visibleGroups.has(group));
    if (active) this.setActiveTabById(active.dataset.faqGroupId);
  }

  getStickyOffset() {
    const root = getComputedStyle(document.documentElement);
    // Match the bar's sticky top: header only (announcement has scrolled away by now).
    const header = parseFloat(root.getPropertyValue('--header-height-fixed')) || 0;
    // Mobile: the tab rail is a sticky top bar over the content → groups must clear it.
    // Desktop: the rail is a side column → no vertical clearance needed.
    const tabsHeight = responsive.matchesBreakpoint('lap_and_up')
      ? 0
      : this.tabsRail?.offsetHeight ?? 0;
    return Math.round(header + tabsHeight + 16);
  }

  scrollToGroup(targetId) {
    const targetGroup = this.groups.find((group) => group.dataset.faqGroupId === targetId);
    if (!targetGroup || targetGroup.getAttribute('aria-hidden') === 'true') {
      return;
    }

    const y = Math.max(
      0,
      targetGroup.getBoundingClientRect().top + window.scrollY - this.getStickyOffset()
    );
    this.#suppressObserverUntil = performance.now() + OBSERVER_SUPPRESS_MS;
    this.#animateScrollTo(y);
  }

  #animateScrollTo(targetY) {
    cancelAnimationFrame(this.#scrollRAF);

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const startY = window.scrollY;
    const distance = targetY - startY;

    if (reduceMotion || Math.abs(distance) < 1) {
      window.scrollTo(0, targetY);
      return;
    }

    const start = performance.now();
    // Quadratic ease-in: gentle start accelerating into the target over SCROLL_DURATION_MS.
    const easeIn = (t) => t * t;
    const step = (now) => {
      const t = Math.min(1, (now - start) / SCROLL_DURATION_MS);
      window.scrollTo(0, startY + distance * easeIn(t));
      if (t < 1) {
        this.#scrollRAF = requestAnimationFrame(step);
      }
    };
    this.#scrollRAF = requestAnimationFrame(step);
  }

  setNoResultsVisible(isVisible) {
    this.classList.toggle('is-no-results', isVisible);
    this.noResults.forEach((el) => {
      el.classList.toggle('is-hidden', !isVisible);
    });
  }

  filterBySearch() {
    if (!this.searchTerm) {
      this.resetSearchVisibility();
      return;
    }

    let hasAnyVisible = false;
    const matchResults = [];

    this.#faqItems.forEach((entry) => {
      const { item, group, questionText, answerText, groupHeading } = entry;
      const matches =
        questionText.includes(this.searchTerm) ||
        answerText.includes(this.searchTerm) ||
        groupHeading.includes(this.searchTerm);
      matchResults.push({ item, group, matches });
      if (matches) {
        hasAnyVisible = true;
      }
    });

    if (!hasAnyVisible) {
      this.#renderNoResults();
      this.setNoResultsVisible(true);
      this.groups.forEach((group) => {
        group.setAttribute('aria-hidden', 'false');
      });
      this.#faqItems.forEach(({ item }) => {
        item.classList.remove('is-hidden');
      });
      this.#announce(0);
      return;
    }

    this.setNoResultsVisible(false);
    // No section is "current" while a search filter is active.
    this.tabs.forEach((tab) => tab.setAttribute('aria-current', 'false'));

    const visibleGroups = new Set();
    matchResults.forEach(({ item, group, matches }) => {
      item.classList.toggle('is-hidden', !matches);
      if (matches) {
        visibleGroups.add(group);
      }
    });

    this.groups.forEach((group) => {
      group.setAttribute('aria-hidden', visibleGroups.has(group) ? 'false' : 'true');
    });

    this.#announce(matchResults.filter((result) => result.matches).length);
  }

  resetSearchVisibility() {
    this.setNoResultsVisible(false);
    this.groups.forEach((group) => {
      group.setAttribute('aria-hidden', 'false');
    });
    this.#faqItems.forEach(({ item }) => {
      item.classList.remove('is-hidden');
    });
    this.#announce('');
    // Active tab re-syncs from the IntersectionObserver once groups are visible again.
  }

  // Announce the filter outcome to screen readers via the persistent live region ('' clears it on
  // reset). textContent keeps the count / typed term XSS-safe, like #renderNoResults.
  #announce(count) {
    if (!this.searchStatus) return;
    if (count === '') {
      this.searchStatus.textContent = '';
      return;
    }
    if (count > 0) {
      const template = this.searchStatus.dataset.faqResultsTemplate ?? '';
      this.searchStatus.textContent = template.replaceAll('%%COUNT%%', String(count));
    } else {
      const term = this.searchInput?.value.trim() ?? '';
      const template = this.searchStatus.dataset.faqEmptyTemplate ?? '';
      this.searchStatus.textContent = template.replaceAll('%%TERM%%', term);
    }
  }

  #cacheDom() {
    this.searchInput = this.querySelector('[data-faq-search-input]');
    this.searchStatus = this.querySelector('[data-faq-search-status]');
    this.noResults = Array.from(this.querySelectorAll('[data-faq-no-results]'));
    this.tabs = Array.from(this.querySelectorAll('.faq__tab'));
    this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
    this.tabsRail = this.querySelector('.faq-tabs');
    this.swiperEl = this.querySelector('.faq-tabs swiper-init');
    this.#faqItems = [];

    this.groups.forEach((group) => {
      // Group heading (e.g. "Shopping online") is searchable too: typing it surfaces every
      // item in that group. Captured once per group, reused for each of its items.
      const groupHeading = group.querySelector(ITEM_SELECTORS.groupHeading)?.textContent?.toLowerCase() ?? '';
      group.querySelectorAll('[data-faq-item]').forEach((item) => {
        this.#faqItems.push({
          item,
          group,
          questionText: item.querySelector(ITEM_SELECTORS.question)?.textContent?.toLowerCase() ?? '',
          answerText: item.querySelector(ITEM_SELECTORS.answer)?.textContent?.toLowerCase() ?? '',
          groupHeading,
        });
      });
    });
  }

  #renderNoResults() {
    // Inject the user's search term (original casing) into the localized message:
    // "No results found for “<term>”. ...". textContent keeps it XSS-safe.
    const term = this.searchInput?.value.trim() ?? '';
    this.noResults.forEach((el) => {
      const textEl = el.querySelector('[data-faq-no-results-text]');
      const template = el.dataset.faqNoResultsTemplate ?? '';
      if (textEl) textEl.textContent = template.replaceAll('%%TERM%%', term);
    });
  }
}

if (!customElements.get('faq-section')) {
  customElements.define('faq-section', FaqSection);
}
